import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';

// Reusable date formatter YYYY-MM-DD
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDatePtBr = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return raw;
  }

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

// Helpers to compute periods using only native Date
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, d.getDate());

// Fiscal helpers
const clampToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// Default options list builder
const buildOptions = (_fiscalStartMonth = 0) => {
  const today = clampToday();

  const thisMonthStart = startOfMonth(today);
  const thisMonthEnd = endOfMonth(today);

  const lastMonthDate = addMonths(today, -1);
  const lastMonthStart = startOfMonth(lastMonthDate);
  const lastMonthEnd = endOfMonth(lastMonthDate);

  const previousYear = today.getFullYear() - 1;
  const previousYearStart = new Date(previousYear, 0, 1);
  const previousYearEnd = new Date(previousYear, 11, 31);
  const last6MonthsStart = startOfMonth(addMonths(today, -5)); // -5 para ter 6 meses (atual + 5 anteriores)

  const thisYearStart = startOfYear(today);

  return [
    {
      id: 'mtd',
      label: 'Até agora (mês)',
      range: { start: fmt(thisMonthStart), end: fmt(today) },
    },
    {
      id: 'mes_passado',
      label: 'Mês passado',
      range: { start: fmt(lastMonthStart), end: fmt(lastMonthEnd) },
    },
    {
      id: 'last_year',
      label: 'Ano passado',
      range: { start: fmt(previousYearStart), end: fmt(previousYearEnd) },
    },
    {
      id: 'last_6_months',
      label: 'Último semestre',
      range: { start: fmt(last6MonthsStart), end: fmt(thisMonthEnd) },
    },
    {
      id: 'ytd',
      label: 'Este ano até agora',
      range: { start: fmt(thisYearStart), end: fmt(today) },
    },
    {
      id: 'custom',
      label: 'Consulta personalizada',
      range: null,
    },
  ];
};

/**
 * DateRangeFilterBlur - componente de filtros de data com efeito de blur durante mudanças.
 * Similar ao DateRangeFilter original, mas com overlay de blur quando está carregando.
 *
 * Props:
 * - value: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD', presetId?: string }
 * - onChange: (payload) => void
 * - fiscalStartMonth?: number (0-11) - padrão 0 (Janeiro)
 * - className?: string
 * - loading?: boolean - se está carregando dados
 */
export default function DateRangeFilterBlur({
  value,
  onChange,
  fiscalStartMonth = 0,
  className = '',
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const containerRef = useRef(null);

  const options = useMemo(() => buildOptions(fiscalStartMonth), [fiscalStartMonth]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentLabel = useMemo(() => {
    if (value?.presetId) {
      const found = options.find((o) => o.id === value.presetId);
      if (found) return found.label;
    }
    if (value?.start && value?.end) {
      return `${formatDatePtBr(value.start)} - ${formatDatePtBr(value.end)}`;
    }
    return 'Selecionar período';
  }, [value, options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [query, options]);

  const handleSelect = (opt) => {
    if (opt.id === 'custom') {
      setCustomStart(value?.start || '');
      setCustomEnd(value?.end || '');
      // keep dropdown open to show custom inputs
      return;
    }
    onChange?.({ start: opt.range.start, end: opt.range.end, presetId: opt.id });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customStart || !customEnd) return;
    onChange?.({ start: customStart, end: customEnd, presetId: 'custom' });
    setOpen(false);
  };

  return (
    <>
      {/* Loading overlay quando mudando filtro de data */}
      {loading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm bg-black/20">
          <div
            className="rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)] px-6 py-5"
            style={{ boxShadow: 'var(--shadow)' }}
          >
            <div className="flex items-center gap-4">
              <div className="loader-elegant" aria-hidden="true"></div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--text-muted)]">
                  Carregando
                </p>
                <p className="mt-1 text-sm text-[color:var(--text-primary)]">
                  Atualizando dados do período
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div ref={containerRef} className={`relative inline-block ${className}`}>
        <button
          type="button"
          onClick={() => !loading && setOpen((v) => !v)}
          className={`w-full md:w-auto inline-flex min-h-[40px] items-center gap-2.5 rounded-[12px] border px-3.5 py-2 text-[13px] font-medium shadow-sm transition-colors focus:outline-none ${
            loading
              ? 'cursor-not-allowed border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/65 text-[color:var(--text-muted)]'
              : 'border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/55'
          }`}
          disabled={loading}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {loading && (
            <svg className="h-4 w-4 animate-spin text-[color:var(--accent)]" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
          )}
          {!loading && <CalendarDays className="h-4 w-4 text-[color:var(--accent)]" />}
          <span className="truncate whitespace-nowrap max-w-[60vw] md:max-w-none">{currentLabel}</span>
          <ChevronDown className={`h-4 w-4 text-[color:var(--text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div
            className="absolute z-50 mt-2 transform
                       left-1/2 -translate-x-1/2 sm:translate-x-0 sm:left-auto sm:right-0
                       w-[calc(100vw-2rem)] sm:w-96 max-w-[calc(100vw-2rem)] sm:max-w-none
                       rounded-md bg-[color:var(--surface)] shadow-lg ring-1 ring-[color:var(--border)] focus:outline-none"
          >
            <div className="p-2 border-b border-[color:var(--border)]">
              <input
                type="text"
                placeholder="Buscar intervalos rápidos"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
              />
            </div>
            <ul className="max-h-64 overflow-auto p-1" role="listbox">
              {filtered.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm rounded-md text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]"
                    onClick={() => handleSelect(opt)}
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Custom Range Section */}
            <div className="border-t border-[color:var(--border)] p-3">
              <div className="text-xs text-[color:var(--text-muted)] mb-2">Consulta personalizada</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--text-primary)] focus:outline-none"
                  placeholder="Início"
                />
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--text-primary)] focus:outline-none"
                  placeholder="Fim"
                />
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="px-3 py-2 text-sm rounded-md border border-[color:var(--border)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]"
                  onClick={() => setOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="px-3 py-2 text-sm rounded-md text-white disabled:opacity-50"
                  style={{ backgroundColor: 'var(--accent)' }}
                  disabled={!customStart || !customEnd}
                  onClick={applyCustom}
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
